// src/pages/TrackCreation/AccessSecurityScreen.tsx
import React, { FC, useState, useEffect, useMemo } from "react";
import activeRadio from "../../../../assets/images/radio_active.png";
import inactiveRadio from "../../../../assets/images/radio_inactive.png";
import { useTranslation } from "react-i18next";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import RadioButton from "../../../../components/common/RadioButton";
import Button from "../../../../components/ui/Button";
import NumberInput from "../../../../components/common/NumberInput";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import { AccessSecurityScreenProps } from "../../../../types/screensTypes";
import Dropdown from "../../../../components/common/Dropdown";
import stepStyles from "../createStep.module.css";
import styles from "./AccessSecurity.module.css";

const AccessSecurityScreen: FC<AccessSecurityScreenProps> = ({ navigateToNext }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { trackData, updateTrackData } = useTrackCreation();

    const pricingOptions = useMemo(
        () => [
            { label: "0.49€", value: "0.49" },
            { label: "0.99€", value: "0.99" },
            { label: "1.99€", value: "1.99" },
            { label: "2.49€", value: "2.49" },
            { label: "2.99€", value: "2.99" },
            { label: "3.49€", value: "3.49" },
            { label: "3.99€", value: "3.99" },
            { label: "4.49€", value: "4.49" },
            { label: "4.50€", value: "4.50" },
            { label: "4.99€", value: "4.99" },
            { label: "5.49€", value: "5.49" },
            { label: "5.99€", value: "5.99" },
            { label: "6.49€", value: "6.49" },
            { label: "6.99€", value: "6.99" },
            { label: "7.49€", value: "7.49" },
            { label: "7.99€", value: "7.99" },
            { label: "8.49€", value: "8.49" },
            { label: "8.99€", value: "8.99" },
            { label: "9.49€", value: "9.49" },
            { label: "9.99€", value: "9.99" },
        ],
        [],
    );

    // Used to disable the in-app purchase
    const isColorbus = useMemo(() => {
        return window.location.hostname.includes("colorbus");
    }, []);

    const isVedettesDeParis = useMemo(() => {
        return window.location.hostname.includes("vedettesdeparis");
    }, []);

    const isThoiry = useMemo(() => {
        const h = window.location.hostname;
        return h.includes("thoiry") || h.includes("wowgroupe.rewindstories");
    }, []);

    // Derive from trackData on mount, then keep in sync once/if it loads later
    const [isPaid, setIsPaid] = useState<boolean>(
        () => Boolean(trackData?.isPaid) || Number(trackData?.price ?? 0) > 0,
    );

    const disableInAppPurchaseToggle = isPaid && (isColorbus || isVedettesDeParis || isThoiry);

    useEffect(() => {
        if (!trackData) return;

        const fromFlag = Boolean(trackData.isPaid);
        const fromPrice = Boolean(trackData.price !== undefined && Number(trackData.price) > 0);

        setIsPaid(fromFlag || fromPrice);
    }, [trackData]);

    // Free by default: fill blank price/promotion with "0" (only when blank, so a chosen price is never overwritten)
    useEffect(() => {
        if (!trackData) return;
        const isBlank = (v: unknown) => v === undefined || v === null || v === "";
        const free = !trackData.isPaid && !(Number(trackData.price) > 0);
        if (free && isBlank(trackData.price)) updateTrackData("price", "0");
        if (isBlank(trackData.promotion)) updateTrackData("promotion", "0");
    }, [trackData, updateTrackData]);

    const handlePaymentOptionChange = (value: string): void => {
        const paying = value === "paying";
        setIsPaid(paying);
        updateTrackData("isPaid", paying);
        if (!paying) {
            updateTrackData("price", "0");
            updateTrackData("promotion", "0");
        }
    };

    // Force in-app purchase to false if origin is colorbus or vedettesdeparis and tour is isPaid
    useEffect(() => {
        if (disableInAppPurchaseToggle && trackData.unlockModeFree !== true) {
            updateTrackData("unlockModeFree", true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableInAppPurchaseToggle]);

    // Ensure price stays within dropdown options when in paid + in-app mode
    useEffect(() => {
        if (trackData?.unlockModeFree === false) {
            const validPricingValues = pricingOptions.map((opt) => opt.value);
            const current = trackData.price?.toString();
            if (current && current !== "0" && !validPricingValues.includes(current)) {
                updateTrackData("price", pricingOptions[0].value);
            }
        }
    }, [trackData, updateTrackData, pricingOptions]);

    const isMuseum = trackData?.type === "iduniquemusee";

    // Automatically set forceDownload to 'force' for museum tracks
    useEffect(() => {
        if (isMuseum && trackData.forceDownload !== "force") {
            updateTrackData("forceDownload", "force");
        }
    }, [isMuseum, trackData.forceDownload, updateTrackData]);

    // Set default value to 'recommend' for non-museum tracks if not already set
    useEffect(() => {
        if (!isMuseum && trackData.forceDownload === undefined) {
            updateTrackData("forceDownload", "recommend");
        }
    }, [isMuseum, trackData.forceDownload, updateTrackData]);

    return (
        <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step} ${styles.root}`}>
            <TitleSubtitle title={t("wouldYouLikeToPayTour")} subtitle="" layout="access" />

            <div className={styles.radioGroup}>
                <RadioButton
                    label={t("free")}
                    subtitle={t("freeExplanation")}
                    checked={!isPaid}
                    onChange={() => handlePaymentOptionChange("free")}
                    activeImage={activeRadio}
                    inactiveImage={inactiveRadio}
                />
                <RadioButton
                    label={t("paid")}
                    subtitle={t("paidExplanation")}
                    checked={isPaid}
                    onChange={() => handlePaymentOptionChange("paying")}
                    activeImage={activeRadio}
                    inactiveImage={inactiveRadio}
                />
            </div>

            {/* If track isPaid then we use unlockModeFree to determine if the purchase is in app (false) or external (true) */}
            {isPaid && (
                <>
                    <TitleSubtitle title={t("howPurchaseInApp")} subtitle="" layout="access" />

                    <div className={`${styles.radioGroup} flex-shrink-zero`}>
                        <RadioButton
                            label={t("yes")}
                            subtitle={t("howPurchaseInAppConstraints")}
                            checked={trackData.unlockModeFree === false}
                            onChange={() => updateTrackData("unlockModeFree", false)}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                            disabled={disableInAppPurchaseToggle}
                            tooltip={disableInAppPurchaseToggle ? t("onlyExternalPurchaseAvailable") : null}
                        />
                        <RadioButton
                            label={t("no")}
                            subtitle={t("howPurchaseInAppNoConstraints")}
                            checked={trackData.unlockModeFree === true}
                            onChange={() => updateTrackData("unlockModeFree", true)}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                            disabled={disableInAppPurchaseToggle}
                            tooltip={disableInAppPurchaseToggle ? t("onlyExternalPurchaseAvailable") : null}
                        />
                    </div>
                </>
            )}

            {isPaid && trackData?.unlockModeFree === false && (
                <>
                    <TitleSubtitle
                        title={t("price")}
                        subtitle={t("thisPriceWillBeDisplayedToVisitorsWithinTheApplication")}
                        layout="access"
                    />
                    <Dropdown
                        label=""
                        value={trackData.price?.toString() || ""}
                        options={pricingOptions}
                        onChange={(v) => updateTrackData("price", parseFloat(v))}
                        placeholder={t("selectPrice")}
                        loading={false}
                        error={false}
                    />
                </>
            )}

            {isPaid && trackData?.unlockModeFree === true && (
                <>
                    <TitleSubtitle
                        title={t("price")}
                        subtitle={t("thisPriceWillBeDisplayedToVisitorsWithinTheApplication")}
                        layout="access"
                    />
                    <NumberInput
                        placeholder={t("selectPrice")}
                        value={trackData.price || ""}
                        onChange={(e) => updateTrackData("price", e.target.value)}
                    />
                </>
            )}

            {/* If track is not isPaid then we use unlockModeFree to determine if users need a qr code (false) or can have a free access (true) */}
            {!isPaid && (
                <>
                    <TitleSubtitle title={t("whoCanAccessYourTour")} subtitle="" layout="access" />

                    <div className={styles.radioGroup}>
                        <RadioButton
                            label={t("onlyUsersWhoHaveScannedYourQRCode")}
                            subtitle={t("optionQRcode")}
                            checked={trackData.unlockModeFree === false}
                            onChange={() => updateTrackData("unlockModeFree", false)}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                        />
                        <RadioButton
                            label={t("everyUserWhoDownloadsTheApp")}
                            subtitle={t("optionAllUsers")}
                            checked={trackData.unlockModeFree === true}
                            onChange={() => updateTrackData("unlockModeFree", true)}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                        />
                    </div>
                </>
            )}

            {!isMuseum && (
                <>
                    <TitleSubtitle title={t("wouldYouLikeToManageDownloadBeforeVisit")} subtitle="" layout="access" />

                    <div className={styles.radioGroup}>
                        <RadioButton
                            label={t("doNotSuggestDownload")}
                            subtitle={t("usersStartVisitWithoutDownloadSuggestion")}
                            checked={trackData.forceDownload === "none"}
                            onChange={() => updateTrackData("forceDownload", "none")}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                        />
                        <RadioButton
                            label={t("recommendDownload")}
                            subtitle={t("usersWillBeRecommendedToDownloadButCanContinue")}
                            checked={trackData.forceDownload === "recommend"}
                            onChange={() => updateTrackData("forceDownload", "recommend")}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                        />
                        <RadioButton
                            label={t("forceDownload")}
                            subtitle={t("usersMustDownloadBeforeStartingVisit")}
                            checked={trackData.forceDownload === "force"}
                            onChange={() => updateTrackData("forceDownload", "force")}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                        />
                    </div>
                </>
            )}

            {isMuseum && (
                <>
                    <TitleSubtitle title={t("howDoUsersAccessTourOnsite")} subtitle="" layout="access" />

                    <div className={styles.radioGroup}>
                        <RadioButton
                            label={t("qrCode")}
                            subtitle={t("qrcodeExplanation")}
                            checked={trackData.museumSearchQrcode === true}
                            onChange={() => updateTrackData("museumSearchQrcode", true)}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                        />
                        <RadioButton
                            label={t("number")}
                            subtitle={t("numberExplanation")}
                            checked={trackData.museumSearchQrcode === false}
                            onChange={() => updateTrackData("museumSearchQrcode", false)}
                            activeImage={activeRadio}
                            inactiveImage={inactiveRadio}
                        />
                    </div>
                </>
            )}

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("continue")}
                    onClick={navigateToNext}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>
        </div>
    );
};

export default AccessSecurityScreen;
